import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch, batch, shallowEqual } from 'react-redux';
import { RootState } from '../services/rootReducer';
import { AppDispatch } from '../services/store';
import { setFacetSelected } from '../services/Facets_Slice';
import { setFreeTextSearch } from '../services/Search_Slice';
import { setLatestDateInfoFromUrl, clearLatestDateInfo } from '../services/FacetNavigation_Slice';
import {
    setType,
    setSortField,
    setSortOrder,
    setCalRangeUsed,
    setCalRangeValue,
    setCursors,
    setPageRangeUsed,
    setModalOpen,
    setModalDate,
    setModalMission,
    setDateIntervalFrom,
    setDateIntervalTo,
    viewType,
    calRangeType,
    sortFieldType,
    sortType
} from '../services/View_Slice';
import { format, startOfMonth } from 'date-fns';
import { getMonday } from '../components/utils/dateHelper';
import {
    PARAM_MISSION,
    PARAM_INSTRUMENT,
    PARAM_CALENDARTYPE,
    PARAM_LEVEL,
    PARAM_PRODUCTTYPE,
    PARAM_DOCUMENTTYPE,
    PARAM_SORTBY,
    PARAM_SORTBYDIRECTION,
    PARAM_SHOW,
    PARAM_VIEWBY,
    PARAM_DATESTART,
    PARAM_SEARCHBAR,
    PARAM_VIEWTYPE,
    PARAM_PAGE,
    PARAM_MODAL,
    PARAM_MODAL_DATE,
    PARAM_MODAL_MISSION,
    PARAM_DATE_FROM,
    PARAM_DATE_TO,
    parseQueryStringForFacets
} from '../utils/urlParser';

/**
 * Custom hook to synchronize Redux state with URL parameters
 * This enables URL-based sharing of application state
 */
export const useUrlSync = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();

    // Navigation state to prevent infinite loops (popstate → state → URL → popstate)
    const isNavigatingRef = useRef(false);

    // Track previous facets to determine if this is a significant change
    const prevFacetsRef = useRef<string[]>([]);

    // Get relevant state from Redux store
    const facetSelected = useSelector((state: RootState) => state.Facets.facetSelected);
    const freeTextSearch = useSelector((state: RootState) => state.Search.freeTextSearch);
    const {
        viewType: currentViewType,
        listSortedFeld,
        listSortedOrder,
        calRangeUsed,
        calRangeValue,
        pageRangeUsed,
        cursorStart,
        urlConstraintsUsed,
        modalOpen,
        modalDate,
        modalMission,
        dateIntervalFrom,
        dateIntervalTo,
        isAutoNavigating
    } = useSelector(
        (state: RootState) => ({
            viewType: state.View.viewType,
            listSortedFeld: state.View.listSortedFeld,
            listSortedOrder: state.View.listSortedOrder,
            calRangeUsed: state.View.calRangeUsed,
            calRangeValue: state.View.calRangeValue,
            pageRangeUsed: state.View.pageRangeUsed,
            cursorStart: state.View.cursorStart,
            urlConstraintsUsed: state.View.urlConstraintsUsed,
            modalOpen: state.View.modalOpen,
            modalDate: state.View.modalDate,
            modalMission: state.View.modalMission,
            dateIntervalFrom: state.View.dateIntervalFrom,
            dateIntervalTo: state.View.dateIntervalTo,
            isAutoNavigating: state.View.isAutoNavigating,
        }),
        shallowEqual
    );

    // Handle browser back/forward buttons
    useEffect(() => {
        const handlePopstate = () => {
            // Set flag to prevent URL update effect from triggering
            isNavigatingRef.current = true;

            const searchParams = new URLSearchParams(window.location.search);

            batch(() => {
            // Restore facets from URL
            const facets = parseQueryStringForFacets(searchParams);
            dispatch(setFacetSelected(facets));
            prevFacetsRef.current = facets;

            // Update latestDateInfo based on restored state
            // This ensures the info bar shows correct "Latest entry" info after back/forward navigation
            const dateStartParam = searchParams.get(PARAM_DATESTART);
            if (facets.length > 0 && dateStartParam) {
                dispatch(setLatestDateInfoFromUrl({
                    date: dateStartParam,
                    filters: facets
                }));
            } else if (facets.length === 0) {
                dispatch(clearLatestDateInfo());
            }

            // Restore search text
            const searchText = searchParams.get(PARAM_SEARCHBAR) || '';
            dispatch(setFreeTextSearch(searchText));

            // Restore view type
            const viewTypeParam = searchParams.get(PARAM_VIEWTYPE);
            if (viewTypeParam === 'list') {
                dispatch(setType(viewType.List));
            } else {
                dispatch(setType(viewType.Calendar));
            }

            // Restore sort settings
            const sortByParam = searchParams.get(PARAM_SORTBY);
            if (sortByParam && sortByParam in sortFieldType) {
                dispatch(setSortField(sortFieldType[sortByParam as keyof typeof sortFieldType]));
            } else {
                dispatch(setSortField(sortFieldType.date));
            }

            const sortDirParam = searchParams.get(PARAM_SORTBYDIRECTION);
            if (sortDirParam === 'asc') {
                dispatch(setSortOrder(sortType.ascending));
            } else {
                dispatch(setSortOrder(sortType.descending));
            }

            // Restore calendar range type
            const viewByParam = searchParams.get(PARAM_VIEWBY);
            let calRange = calRangeType.month; // default
            if (viewByParam === 'year') calRange = calRangeType.year;
            else if (viewByParam === 'week') calRange = calRangeType.week;
            else if (viewByParam === 'day') calRange = calRangeType.day;
            dispatch(setCalRangeUsed(calRange));

            // Restore calendar date (reuses dateStartParam from above)
            if (dateStartParam) {
                dispatch(setCalRangeValue(dateStartParam));
            } else {
                // Reset to default date when dts parameter is absent (e.g., back to base URL)
                // Determine default based on the current/restored view type
                let defaultDate: string;
                switch (calRange) {
                    case calRangeType.week:
                        defaultDate = format(getMonday(new Date()), 'yyyy-MM-dd');
                        break;
                    case calRangeType.year:
                        defaultDate = format(new Date(), 'yyyy');
                        break;
                    case calRangeType.month:
                    default:
                        defaultDate = format(startOfMonth(new Date()), 'yyyy-MM');
                        break;
                }
                dispatch(setCalRangeValue(defaultDate));
            }

            // Restore pagination
            const showParam = searchParams.get(PARAM_SHOW);
            const pageRangeVal = showParam ? parseInt(showParam, 10) : 10;
            dispatch(setPageRangeUsed(pageRangeVal));

            const pageParam = searchParams.get(PARAM_PAGE);
            const currentPage = pageParam ? parseInt(pageParam, 10) : 0;
            dispatch(setCursors({
                cursorStart: currentPage * pageRangeVal,
                cursorRows: pageRangeVal
            }));

            // Restore modal state
            const modalParam = searchParams.get(PARAM_MODAL);
            dispatch(setModalOpen(modalParam === 'open'));
            dispatch(setModalDate(searchParams.get(PARAM_MODAL_DATE) || ''));
            dispatch(setModalMission(searchParams.get(PARAM_MODAL_MISSION) || ''));

            // Restore date interval filter
            dispatch(setDateIntervalFrom(searchParams.get(PARAM_DATE_FROM)));
            dispatch(setDateIntervalTo(searchParams.get(PARAM_DATE_TO)));
            });

            // Clear navigation flag after state updates propagate
            // Using setTimeout to allow React's state batching to complete
            setTimeout(() => {
                isNavigatingRef.current = false;
            }, 0);
        };

        window.addEventListener('popstate', handlePopstate);
        return () => window.removeEventListener('popstate', handlePopstate);
    }, [dispatch]);

    useEffect(() => {
        // Only update URL if we've already processed initial URL constraints
        // This prevents infinite loops during initial load
        if (!urlConstraintsUsed) {
            return;
        }

        const searchParams = new URLSearchParams();

        // Add facet parameters
        const facetMap: { [key: string]: string } = {
            'mission_ss': PARAM_MISSION,
            'instrument_ss': PARAM_INSTRUMENT,
            'calendartype_ss': PARAM_CALENDARTYPE,
            'level_ss': PARAM_LEVEL,
            'producttype_ss': PARAM_PRODUCTTYPE,
            'documenttype_ss': PARAM_DOCUMENTTYPE
        };

        // Clear existing facet params
        Object.values(facetMap).forEach(param => {
            searchParams.delete(param);
        });

        // Add selected facets to URL
        facetSelected.forEach(facet => {
            const [value, field] = facet.split('@');
            const paramName = facetMap[field];
            if (paramName && value) {
                // Support multiple values for the same facet type
                const existingValues = searchParams.getAll(paramName);
                if (!existingValues.includes(value)) {
                    searchParams.append(paramName, value);
                }
            }
        });

        // Add search text
        if (freeTextSearch && freeTextSearch.trim()) {
            searchParams.set(PARAM_SEARCHBAR, freeTextSearch.trim());
        } else {
            searchParams.delete(PARAM_SEARCHBAR);
        }

        // Add sort parameters only if they differ from defaults
        if (listSortedFeld !== undefined && listSortedFeld !== sortFieldType.date) {
            const sortFieldName = Object.keys(sortFieldType).find(
                key => sortFieldType[key as keyof typeof sortFieldType] === listSortedFeld
            );
            if (sortFieldName) {
                searchParams.set(PARAM_SORTBY, sortFieldName);
            }
        }

        if (listSortedOrder !== undefined && listSortedOrder !== sortType.descending) {
            const sortDirection = listSortedOrder === sortType.ascending ? 'asc' : 'desc';
            searchParams.set(PARAM_SORTBYDIRECTION, sortDirection);
        }

        // Add pagination
        if (pageRangeUsed && pageRangeUsed !== 10) { // 10 is default
            searchParams.set(PARAM_SHOW, pageRangeUsed.toString());
        } else {
            searchParams.delete(PARAM_SHOW);
        }
        
        // Add current page if in list view and not on first page
        if (currentViewType === viewType.List && cursorStart > 0) {
            const currentPage = Math.floor(cursorStart / pageRangeUsed);
            if (currentPage > 0) {
                searchParams.set(PARAM_PAGE, currentPage.toString());
            }
        }

        // Add view type only if it differs from default (Calendar)
        if (currentViewType === viewType.List) {
            searchParams.set(PARAM_VIEWTYPE, 'list');
        } else {
            searchParams.delete(PARAM_VIEWTYPE);
        }

        // Add date range only if it differs from default (month)
        if (calRangeUsed !== undefined && calRangeUsed !== calRangeType.month) {
            let viewByValue = '';
            switch (calRangeUsed) {
                case calRangeType.year:
                    viewByValue = 'year';
                    break;
                case calRangeType.week:
                    viewByValue = 'week';
                    break;
                case calRangeType.day:
                    viewByValue = 'day';
                    break;
            }
            if (viewByValue) {
                searchParams.set(PARAM_VIEWBY, viewByValue);
            }
        }

        // Add date start only if it differs from the default for each view type
        let shouldAddDateParam = false;

        if (calRangeValue) {
            switch (calRangeUsed) {
                case calRangeType.month:
                    const currentMonth = format(startOfMonth(new Date()), 'yyyy-MM');
                    shouldAddDateParam = calRangeValue !== currentMonth;
                    break;
                case calRangeType.week:
                    const currentWeekStart = format(getMonday(new Date()), 'yyyy-MM-dd');
                    shouldAddDateParam = calRangeValue !== currentWeekStart;
                    break;
                case calRangeType.year:
                    const currentYear = format(new Date(), 'yyyy');
                    shouldAddDateParam = calRangeValue !== currentYear;
                    break;
                default:
                    shouldAddDateParam = true;
            }
        }

        if (shouldAddDateParam) {
            searchParams.set(PARAM_DATESTART, calRangeValue);
        } else {
            searchParams.delete(PARAM_DATESTART);
        }

        // Add modal parameters if modal is open
        if (modalOpen && modalDate && modalMission) {
            searchParams.set(PARAM_MODAL, 'open');
            searchParams.set(PARAM_MODAL_DATE, modalDate);
            searchParams.set(PARAM_MODAL_MISSION, modalMission);
        } else {
            searchParams.delete(PARAM_MODAL);
            searchParams.delete(PARAM_MODAL_DATE);
            searchParams.delete(PARAM_MODAL_MISSION);
        }

        // Add date interval filter parameters
        if (dateIntervalFrom && dateIntervalFrom.trim()) {
            searchParams.set(PARAM_DATE_FROM, dateIntervalFrom.trim());
        } else {
            searchParams.delete(PARAM_DATE_FROM);
        }

        if (dateIntervalTo && dateIntervalTo.trim()) {
            searchParams.set(PARAM_DATE_TO, dateIntervalTo.trim());
        } else {
            searchParams.delete(PARAM_DATE_TO);
        }

        // Build new URL
        const newSearch = searchParams.toString();
        const currentSearch = location.search.replace('?', '');

        // Only update if URL actually changed to avoid unnecessary history entries
        if (newSearch !== currentSearch) {
            const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;

            // Skip URL updates during popstate processing (browser back/forward)
            if (isNavigatingRef.current) {
                return;
            }

            // Determine if this is a significant change that warrants a new history entry
            // Significant: facet changes, view type, sort, calendar date, search text, date interval
            // Minor: pagination changes, modal open/close
            const prevFacetsSorted = [...prevFacetsRef.current].sort().join(',');
            const currentFacetsSorted = [...facetSelected].sort().join(',');
            const facetsChanged = prevFacetsSorted !== currentFacetsSorted;

            // Check if pagination is the only thing that changed (minor change)
            const isPaginationOnlyChange = !facetsChanged &&
                searchParams.has(PARAM_PAGE) !== new URLSearchParams(currentSearch).has(PARAM_PAGE);

            // Check if modal is the only thing that changed (minor change)
            const isModalOnlyChange = !facetsChanged &&
                searchParams.get(PARAM_MODAL) !== new URLSearchParams(currentSearch).get(PARAM_MODAL);

            // Significant changes get pushed to history stack
            // When isAutoNavigating is true (FacetNavigation changing date), only facet changes are significant
            // This prevents double history entries when filter+date change together as one user action
            const currentSearchParams = new URLSearchParams(currentSearch);
            const isSignificantChange = facetsChanged ||
                (!isAutoNavigating && (
                    searchParams.get(PARAM_VIEWTYPE) !== currentSearchParams.get(PARAM_VIEWTYPE) ||
                    searchParams.get(PARAM_SORTBY) !== currentSearchParams.get(PARAM_SORTBY) ||
                    searchParams.get(PARAM_SEARCHBAR) !== currentSearchParams.get(PARAM_SEARCHBAR) ||
                    searchParams.get(PARAM_DATE_FROM) !== currentSearchParams.get(PARAM_DATE_FROM) ||
                    searchParams.get(PARAM_DATE_TO) !== currentSearchParams.get(PARAM_DATE_TO) ||
                    searchParams.get(PARAM_VIEWBY) !== currentSearchParams.get(PARAM_VIEWBY) ||
                    searchParams.get(PARAM_DATESTART) !== currentSearchParams.get(PARAM_DATESTART)
                ));

            if (isSignificantChange && !isPaginationOnlyChange && !isModalOnlyChange) {
                // Create new history entry for significant navigation
                history.push(newUrl);
                prevFacetsRef.current = [...facetSelected];
            } else {
                // Minor change (pagination, modal) - just replace current entry
                history.replace(newUrl);
            }
        }

    }, [
        facetSelected,
        freeTextSearch,
        currentViewType,
        listSortedFeld,
        listSortedOrder,
        calRangeUsed,
        calRangeValue,
        pageRangeUsed,
        cursorStart,
        urlConstraintsUsed,
        modalOpen,
        modalDate,
        modalMission,
        dateIntervalFrom,
        dateIntervalTo,
        isAutoNavigating,
        history,
        location.pathname
        // location.search removed to prevent infinite loop - the effect checks newSearch !== currentSearch internally
    ]);
};
